import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Link from "@mui/material/Link";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";
import ModalBasicLayout from "../../Modal/ModalBasicLayout";
import ContainerModalActions from "../../Containers/ContainerModalActions";
import Accordion from "../../Containers/Accordion";
import TextField from "../../Form/TextField";
import Checkbox from "../../Form/Checkbox";
import IconButton from "../../Buttons/IconButton";
import KeywordsTable from "../Keywords/KeywordsTable";
import SegmentedControl from "../../SegmentedControl/SegmentedControl";
import Loader from "../../Loaders/Loader";
import SelectGoogleApp from "../../Platforms/Google/Campaign/SelectGoogleApp";
import ChipOptional from "../../Chip/ChipOptional";
import { gallerySecondaryCircleSurfaceSx } from "../../Gallery/galleryIconButtonStyles";
import { PRIMARY_COLOR_TEXT } from "../../../utils/colors";
import { getKeywordMatchType } from "../../../utils/string";
import { useTranslationApp } from "../../../lib/i18next";
import { useDispatchApp } from "../../../lib/redux";
import {
  startGetGoogleAdsAccountExclusions,
  startUpdateGoogleAdsAccountExclusions,
} from "../../../actions/adsCampaigns";
import { SimpleAlert } from "../../Alerts/Alerts";
import {
  ALERT_ICON_TYPE_ERROR,
  ALERT_ICON_TYPE_SUCCESS,
  APPLE_APP_STORE,
  GOOGLE_APP_STORE,
} from "../../../utils/constants";
import {
  APP_PLACEHOLDER,
  APP_STORE_ANDROID,
  APP_STORE_IOS,
  APP_STORE_PLACEHOLDER,
  BUTTON_ADD,
  BUTTON_CANCEL,
  BUTTON_DELETE,
  BUTTON_SAVE,
  FIELD_MAX_LENGTH,
  FIELD_TEXT_REPEATED,
  FIELD_THIS_KEYWORD_ALREADY_EXIST_IN_YOUR_LIST,
  FIELD_VIDEO_YOUTUBE_URL_INVALID,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_APP_CATEGORIES,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_APPS,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_CHANNEL,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_CHANNEL_INVALID,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_CHANNELS,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_CONTENT_CATEGORIES,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_EMPTY_ROW,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_EXCLUDED_APPS,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_KEYWORD,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_KEYWORD_INVALID_CHARS,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_KEYWORD_MATCH_SYNTAX,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_KEYWORDS,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_LOAD_ERROR,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_LOADING,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_SAVED,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_SAVE_ERROR,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_SEARCH_CATEGORIES,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_DESCRIPTION,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_TITLE,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_VIDEO,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_VIDEOS,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_YOUTUBE,
  GOOGLE_ADS_CONTENT_LABEL_BELOW_THE_FOLD,
  GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_CONTENT_FOR_FAMILIES,
  GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_GAMES_FIGHTING,
  GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_GAMES_MATURE,
  GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_HEALTH_SENSITIVE,
  GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_HEALTH_SOURCE_UNDETERMINED,
  GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_NEWS_RECENT,
  GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_NEWS_SENSITIVE,
  GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_NEWS_SOURCE_NOT_FEATURED,
  GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_POLITICS,
  GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_RELIGION,
  GOOGLE_ADS_CONTENT_LABEL_EMBEDDED_VIDEO,
  GOOGLE_ADS_CONTENT_LABEL_JUVENILE,
  GOOGLE_ADS_CONTENT_LABEL_LIVE_STREAMING_VIDEO,
  GOOGLE_ADS_CONTENT_LABEL_PARKED_DOMAIN,
  GOOGLE_ADS_CONTENT_LABEL_PROFANITY,
  GOOGLE_ADS_CONTENT_LABEL_SEXUALLY_SUGGESTIVE,
  GOOGLE_ADS_CONTENT_LABEL_SOCIAL_ISSUES,
  GOOGLE_ADS_CONTENT_LABEL_TRAGEDY,
  GOOGLE_ADS_CONTENT_LABEL_VIDEO,
  GOOGLE_ADS_CONTENT_LABEL_VIDEO_NOT_YET_RATED,
  GOOGLE_ADS_CONTENT_LABEL_VIDEO_RATING_DV_G,
  GOOGLE_ADS_CONTENT_LABEL_VIDEO_RATING_DV_MA,
  GOOGLE_ADS_CONTENT_LABEL_VIDEO_RATING_DV_PG,
  GOOGLE_ADS_CONTENT_LABEL_VIDEO_RATING_DV_T,
} from "../../../i18n/keysTranslations";
import { getGoogleAdsAppCategoryLabel } from "./googleAdsAppCategoryLabels";

const CONTENT_LABEL_KEYS = {
  BELOW_THE_FOLD: GOOGLE_ADS_CONTENT_LABEL_BELOW_THE_FOLD,
  BRAND_SUITABILITY_CONTENT_FOR_FAMILIES:
    GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_CONTENT_FOR_FAMILIES,
  BRAND_SUITABILITY_GAMES_FIGHTING:
    GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_GAMES_FIGHTING,
  BRAND_SUITABILITY_GAMES_MATURE:
    GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_GAMES_MATURE,
  BRAND_SUITABILITY_HEALTH_SENSITIVE:
    GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_HEALTH_SENSITIVE,
  BRAND_SUITABILITY_HEALTH_SOURCE_UNDETERMINED:
    GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_HEALTH_SOURCE_UNDETERMINED,
  BRAND_SUITABILITY_NEWS_RECENT:
    GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_NEWS_RECENT,
  BRAND_SUITABILITY_NEWS_SENSITIVE:
    GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_NEWS_SENSITIVE,
  BRAND_SUITABILITY_NEWS_SOURCE_NOT_FEATURED:
    GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_NEWS_SOURCE_NOT_FEATURED,
  BRAND_SUITABILITY_POLITICS:
    GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_POLITICS,
  BRAND_SUITABILITY_RELIGION:
    GOOGLE_ADS_CONTENT_LABEL_BRAND_SUITABILITY_RELIGION,
  EMBEDDED_VIDEO: GOOGLE_ADS_CONTENT_LABEL_EMBEDDED_VIDEO,
  JUVENILE: GOOGLE_ADS_CONTENT_LABEL_JUVENILE,
  LIVE_STREAMING_VIDEO: GOOGLE_ADS_CONTENT_LABEL_LIVE_STREAMING_VIDEO,
  PARKED_DOMAIN: GOOGLE_ADS_CONTENT_LABEL_PARKED_DOMAIN,
  PROFANITY: GOOGLE_ADS_CONTENT_LABEL_PROFANITY,
  SEXUALLY_SUGGESTIVE: GOOGLE_ADS_CONTENT_LABEL_SEXUALLY_SUGGESTIVE,
  SOCIAL_ISSUES: GOOGLE_ADS_CONTENT_LABEL_SOCIAL_ISSUES,
  TRAGEDY: GOOGLE_ADS_CONTENT_LABEL_TRAGEDY,
  VIDEO: GOOGLE_ADS_CONTENT_LABEL_VIDEO,
  VIDEO_NOT_YET_RATED: GOOGLE_ADS_CONTENT_LABEL_VIDEO_NOT_YET_RATED,
  VIDEO_RATING_DV_G: GOOGLE_ADS_CONTENT_LABEL_VIDEO_RATING_DV_G,
  VIDEO_RATING_DV_MA: GOOGLE_ADS_CONTENT_LABEL_VIDEO_RATING_DV_MA,
  VIDEO_RATING_DV_PG: GOOGLE_ADS_CONTENT_LABEL_VIDEO_RATING_DV_PG,
  VIDEO_RATING_DV_T: GOOGLE_ADS_CONTENT_LABEL_VIDEO_RATING_DV_T,
};

const OPTIONAL_FIELD_RULES = { required: false };

const YOUTUBE_VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_CHANNEL_ID = /^UC[A-Za-z0-9_-]{22}$/;

const isYoutubeHost = (hostname) =>
  /(^|\.)youtube\.com$/i.test(hostname) ||
  /(^|\.)youtube-nocookie\.com$/i.test(hostname) ||
  /^youtu\.be$/i.test(hostname);

const parseYoutubeChannelId = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  try {
    const url = new URL(raw);
    if (!isYoutubeHost(url.hostname)) return "";
    const channelMatch = url.pathname.match(
      /\/channel\/(UC[A-Za-z0-9_-]{22})/i
    );
    if (channelMatch) return channelMatch[1];
    const handleMatch = url.pathname.match(/\/@([A-Za-z0-9._-]{3,30})/);
    if (handleMatch) return `@${handleMatch[1]}`;
    const customMatch = url.pathname.match(
      /\/(?:c|user)\/([A-Za-z0-9._-]{3,30})/i
    );
    if (customMatch) return customMatch[1];
    return "";
  } catch (error) {
    if (YOUTUBE_CHANNEL_ID.test(raw)) return raw;
    if (/^@[A-Za-z0-9._-]{3,30}$/.test(raw)) return raw;
    return "";
  }
};

const parseYoutubeVideoId = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (!raw.includes("/") && !raw.includes(".") && YOUTUBE_VIDEO_ID.test(raw)) {
    return raw;
  }
  try {
    const url = new URL(raw);
    if (!isYoutubeHost(url.hostname)) return "";
    const watchId = url.searchParams.get("v");
    if (watchId && YOUTUBE_VIDEO_ID.test(watchId)) return watchId;
    const pathMatch = url.pathname.match(
      /\/(?:shorts|embed|live|v)\/([A-Za-z0-9_-]{11})/i
    );
    if (pathMatch) return pathMatch[1];
    if (/^youtu\.be$/i.test(url.hostname)) {
      const id = url.pathname.split("/").filter(Boolean)[0];
      if (id && YOUTUBE_VIDEO_ID.test(id)) return id;
    }
  } catch (error) {
    return "";
  }
  return "";
};

const youtubeChannelUrl = (channelId) => {
  const id = String(channelId || "");
  if (id.startsWith("@")) return `https://www.youtube.com/${id}`;
  if (YOUTUBE_CHANNEL_ID.test(id)) {
    return `https://www.youtube.com/channel/${id}`;
  }
  return `https://www.youtube.com/c/${id}`;
};

const youtubeVideoUrl = (videoId) =>
  `https://www.youtube.com/watch?v=${videoId}`;

const parseMobileAppId = (value) => {
  const raw = String(value || "")
    .trim()
    .replace(/^mobileapp::/i, "");
  if (!raw) return "";
  if (/^[12]-/.test(raw)) return raw;
  if (/^\d+$/.test(raw)) return `1-${raw}`;
  if (raw.includes(".")) return `2-${raw}`;
  return "";
};

const mobileAppStoreLabelKey = (appId) => {
  if (String(appId || "").startsWith("2-")) return APP_STORE_ANDROID;
  if (String(appId || "").startsWith("1-")) return APP_STORE_IOS;
  return "";
};

const KEYWORD_MAX_LENGTH = 80;
const INVALID_KEYWORD_CHARS = /[.,/!@%^*()={};~`<>?\\|\\-]/;

const keywordKey = (keyword) =>
  `${String(keyword.matchType || "").toUpperCase()}:${keyword.text.trim().toLowerCase()}`;

const parseExclusionKeyword = (raw) => {
  const trimmed = String(raw || "").trim();
  if (!trimmed) return { ok: false, empty: true };
  if (trimmed.includes("'")) return { ok: false, reason: "match" };

  const opensBracket = trimmed.startsWith("[");
  const closesBracket = trimmed.endsWith("]");
  const opensQuote = trimmed.startsWith('"');
  const closesQuote = trimmed.endsWith('"');
  if (
    opensBracket !== closesBracket ||
    opensQuote !== closesQuote ||
    (opensBracket && opensQuote)
  ) {
    return { ok: false, reason: "match" };
  }

  const parsed = getKeywordMatchType(trimmed);
  const text = String(parsed.text || "").trim();
  if (!text || /[[\]"]/.test(text)) return { ok: false, reason: "match" };
  if (text.length > KEYWORD_MAX_LENGTH) return { ok: false, reason: "length" };
  if (INVALID_KEYWORD_CHARS.test(text)) return { ok: false, reason: "chars" };

  return {
    ok: true,
    text,
    matchType: String(parsed.matchType || "broad").toUpperCase(),
  };
};

const formatStoredKeyword = (keyword) => {
  const text = keyword.text;
  const matchType = String(keyword.matchType || "broad").toLowerCase();
  if (matchType === "exact") return `[${text}]`;
  if (matchType === "phrase") return `"${text}"`;
  return text;
};

const GoogleAdsAccountExclusionsModal = ({
  modalOpen,
  onCloseModal,
  integrationID,
  adAccountID,
  accountName = "",
}) => {
  const { t, i18n } = useTranslationApp();
  const theme = useTheme();
  const dispatch = useDispatchApp();
  const {
    control,
    getValues,
    setValue,
    setError,
    clearErrors,
    watch,
    formState,
  } = useForm({
    defaultValues: {
      keyword: "",
      matchType: "broad",
      channel: "",
      video: "",
      appStore: "",
      appID: "",
      categorySearch: "",
      contentLabelSearch: "",
    },
  });
  const categorySearch = watch("categorySearch");
  const contentLabelSearch = watch("contentLabelSearch");
  const appStore = watch("appStore");
  const [section, setSection] = useState("keywords");
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [keywords, setKeywords] = useState([]);
  const [channels, setChannels] = useState([]);
  const [videos, setVideos] = useState([]);
  const [contentLabels, setContentLabels] = useState([]);
  const [apps, setApps] = useState([]);
  const [appCategories, setAppCategories] = useState([]);

  useEffect(() => {
    if (!modalOpen || !integrationID || !adAccountID) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setLoadError(false);
    setSection("keywords");

    dispatch(
      startGetGoogleAdsAccountExclusions({ integrationID, adAccountID })
    ).then((response) => {
      if (cancelled) return;
      setIsLoading(false);
      if (!response?.ok || !response.data) {
        setLoadError(true);
        return;
      }
      const data = response.data;
      setKeywords(data.negativeKeywords || []);
      setChannels(data.youtubeChannels || []);
      setVideos(data.youtubeVideos || []);
      setContentLabels(data.contentLabels || []);
      setApps(data.mobileApps || []);
      setAppCategories(data.mobileAppCategories || []);
    });

    return () => {
      cancelled = true;
    };
  }, [modalOpen, integrationID, adAccountID, dispatch]);

  const appCategoryLabel = (category) =>
    getGoogleAdsAppCategoryLabel({
      id: category.id,
      name: category.name,
      language: i18n.resolvedLanguage || i18n.language,
    });

  const categoryLanguage = i18n.resolvedLanguage || i18n.language;
  const filteredCategories = useMemo(() => {
    const query = categorySearch.trim().toLowerCase();
    if (!query) return appCategories;
    return appCategories.filter((category) => {
      const translated = getGoogleAdsAppCategoryLabel({
        id: category.id,
        name: category.name,
        language: categoryLanguage,
      });
      return (
        translated.toLowerCase().includes(query) ||
        String(category.name || "")
          .toLowerCase()
          .includes(query)
      );
    });
  }, [appCategories, categorySearch, categoryLanguage]);

  const filteredContentLabels = useMemo(() => {
    const query = String(contentLabelSearch || "")
      .trim()
      .toLowerCase();
    if (!query) return contentLabels;
    return contentLabels.filter((label) => {
      const translated = t(CONTENT_LABEL_KEYS[label.type], {
        defaultValue: label.type,
      });
      return (
        translated.toLowerCase().includes(query) ||
        String(label.type || "")
          .toLowerCase()
          .includes(query)
      );
    });
  }, [contentLabels, contentLabelSearch, t]);

  const saveSection = async (section, payload) => {
    const response = await dispatch(
      startUpdateGoogleAdsAccountExclusions({
        integrationID,
        adAccountID,
        section,
        ...payload,
      })
    );
    if (response?.ok) {
      SimpleAlert({
        title: t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_SAVED),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      return;
    }
    SimpleAlert({
      title: response?.message || t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_SAVE_ERROR),
      icon: ALERT_ICON_TYPE_ERROR,
    });
  };

  const sectionOptions = useMemo(
    () => [
      { value: "keywords", label: t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_KEYWORDS) },
      { value: "youtube", label: t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_YOUTUBE) },
      { value: "apps", label: t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_APPS) },
    ],
    [t]
  );
  const tableKeywords = keywords.map((keyword) => ({
    original: formatStoredKeyword(keyword),
    matchType: String(keyword.matchType || "broad").toLowerCase(),
  }));

  const addKeyword = () => {
    const parsed = parseExclusionKeyword(getValues("keyword"));
    if (parsed.empty) return;
    if (!parsed.ok) {
      const message =
        parsed.reason === "length"
          ? t(FIELD_MAX_LENGTH, { value: KEYWORD_MAX_LENGTH })
          : parsed.reason === "chars"
            ? t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_KEYWORD_INVALID_CHARS)
            : t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_KEYWORD_MATCH_SYNTAX);
      setError("keyword", { message });
      return;
    }
    const next = { text: parsed.text, matchType: parsed.matchType };
    if (keywords.some((keyword) => keywordKey(keyword) === keywordKey(next))) {
      setError("keyword", {
        message: t(FIELD_THIS_KEYWORD_ALREADY_EXIST_IN_YOUR_LIST),
      });
      return;
    }
    clearErrors("keyword");
    setKeywords((current) => [...current, next]);
    setValue("keyword", "");
    setValue("matchType", "broad");
  };

  const onKeywordKeyDown = (event) => {
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
    event.preventDefault();
    addKeyword();
  };

  const addChannel = () => {
    const raw = String(getValues("channel") || "").trim();
    if (!raw) return;
    const channelId = parseYoutubeChannelId(raw);
    if (!channelId) {
      setError("channel", {
        message: t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_CHANNEL_INVALID),
      });
      return;
    }
    if (channels.includes(channelId)) {
      setError("channel", {
        message: t(FIELD_TEXT_REPEATED),
      });
      return;
    }
    clearErrors("channel");
    setChannels((current) => [...current, channelId]);
    setValue("channel", "");
  };

  const addVideo = () => {
    const raw = String(getValues("video") || "").trim();
    if (!raw) return;
    const videoId = parseYoutubeVideoId(raw);
    if (!videoId) {
      setError("video", {
        message: t(FIELD_VIDEO_YOUTUBE_URL_INVALID),
      });
      return;
    }
    if (videos.includes(videoId)) {
      setError("video", {
        message: t(FIELD_TEXT_REPEATED),
      });
      return;
    }
    clearErrors("video");
    setVideos((current) => [...current, videoId]);
    setValue("video", "");
  };

  const onSelectExcludedApp = (app) => {
    if (!app?.appID) return;
    const appId = parseMobileAppId(app.appID);
    const name = String(app.appName || "").trim();
    setValue("appID", "");
    if (!appId) return;
    setApps((current) => {
      if (current.some((item) => item.appId === appId)) return current;
      return [...current, { appId, name }];
    });
  };

  const onDeleteKeywords = async (originals) => {
    setKeywords((current) =>
      current.filter(
        (keyword) => !originals.includes(formatStoredKeyword(keyword))
      )
    );
    return true;
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      title={t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_TITLE, {
        adAccountName: accountName,
      })}
      maxWidth="md"
      fullWidth
    >
      <Box sx={{ pb: 2 }}>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          {t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_DESCRIPTION)}
        </Typography>
        {isLoading ? (
          <Loader
            size={56}
            hasMarginTop={false}
            fullWidth
            hasMessage
            variantMessage="body2"
            message={t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_LOADING)}
          />
        ) : null}
        {loadError ? (
          <Typography variant="body2">
            {t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_LOAD_ERROR)}
          </Typography>
        ) : null}
        {!isLoading && !loadError ? (
          <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <SegmentedControl
                name="accountExclusionsSection"
                options={sectionOptions}
                selectedValue={section}
                onChange={setSection}
              />

              {section === "keywords" ? (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "flex-end",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "text.primary", fontSize: "14px" }}
                      >
                        {t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_KEYWORD)}
                      </Typography>
                      <TextField
                        control={control}
                        name="keyword"
                        rules={OPTIONAL_FIELD_RULES}
                        hiddenLabel
                        variant="filled"
                        fullWidth
                        onKeyDown={onKeywordKeyDown}
                        sx={{
                          "& .MuiFilledInput-root": {
                            height: 48,
                          },
                          "& .MuiFilledInput-input": {
                            height: 48,
                            boxSizing: "border-box",
                            paddingTop: 0,
                            paddingBottom: 0,
                          },
                          "& .MuiFormHelperText-root": {
                            display: "none",
                          },
                        }}
                      />
                    </Box>
                    <IconButton
                      edge={false}
                      tooltipText={BUTTON_ADD}
                      onClick={addKeyword}
                      sx={{
                        ...gallerySecondaryCircleSurfaceSx(theme),
                        width: 48,
                        height: 48,
                        p: 0,
                        borderRadius: "50%",
                        flexShrink: 0,
                        "&:hover": {
                          backgroundColor: `${theme.palette.button.secondary.hover.main} !important`,
                        },
                      }}
                    >
                      <AddIcon
                        sx={{
                          fontSize: 24,
                          color: PRIMARY_COLOR_TEXT,
                          fill: PRIMARY_COLOR_TEXT,
                        }}
                      />
                    </IconButton>
                  </Box>
                  {formState.errors?.keyword?.message ? (
                    <Typography variant="caption" color="error">
                      {formState.errors.keyword.message}
                    </Typography>
                  ) : null}
                  <KeywordsTable
                    isNegativeKeywords
                    keywords={tableKeywords}
                    onDelete={onDeleteKeywords}
                    showEmptyRow
                    emptyRowText={GOOGLE_ADS_ACCOUNT_EXCLUSIONS_EMPTY_ROW}
                  />
                </Box>
              ) : null}

              {section === "youtube" ? (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <Accordion
                    title={t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_CHANNELS)}
                    titleAlign="left"
                    initialValue={false}
                  >
                    <Box
                      sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "text.primary", fontSize: "14px" }}
                        >
                          {t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_CHANNEL)}
                        </Typography>
                        <TextField
                          control={control}
                          name="channel"
                          rules={OPTIONAL_FIELD_RULES}
                          hiddenLabel
                          variant="filled"
                          fullWidth
                          sx={{
                            "& .MuiFilledInput-root": { height: 48 },
                            "& .MuiFilledInput-input": {
                              height: 48,
                              boxSizing: "border-box",
                              paddingTop: 0,
                              paddingBottom: 0,
                            },
                            "& .MuiFormHelperText-root": { display: "none" },
                          }}
                        />
                      </Box>
                      <IconButton
                        edge={false}
                        tooltipText={BUTTON_ADD}
                        onClick={addChannel}
                        sx={{
                          ...gallerySecondaryCircleSurfaceSx(theme),
                          width: 48,
                          height: 48,
                          p: 0,
                          borderRadius: "50%",
                          flexShrink: 0,
                          "&:hover": {
                            backgroundColor: `${theme.palette.button.secondary.hover.main} !important`,
                          },
                        }}
                      >
                        <AddIcon
                          sx={{
                            fontSize: 24,
                            color: PRIMARY_COLOR_TEXT,
                            fill: PRIMARY_COLOR_TEXT,
                          }}
                        />
                      </IconButton>
                    </Box>
                    {formState.errors?.channel?.message ? (
                      <Typography variant="caption" color="error">
                        {formState.errors.channel.message}
                      </Typography>
                    ) : null}
                    {channels.length > 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          mt: 3,
                        }}
                      >
                        {channels.map((channelId) => (
                          <Box
                            key={channelId}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Link
                              href={youtubeChannelUrl(channelId)}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="body2"
                              sx={{ wordBreak: "break-all" }}
                            >
                              {youtubeChannelUrl(channelId)}
                            </Link>
                            <IconButton
                              edge={false}
                              tooltipText={BUTTON_DELETE}
                              onClick={() =>
                                setChannels((current) =>
                                  current.filter((item) => item !== channelId)
                                )
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    ) : null}
                  </Accordion>
                  <Accordion
                    title={t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_VIDEOS)}
                    titleAlign="left"
                    initialValue={false}
                  >
                    <Box
                      sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "text.primary", fontSize: "14px" }}
                        >
                          {t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_VIDEO)}
                        </Typography>
                        <TextField
                          control={control}
                          name="video"
                          rules={OPTIONAL_FIELD_RULES}
                          hiddenLabel
                          variant="filled"
                          fullWidth
                          sx={{
                            "& .MuiFilledInput-root": { height: 48 },
                            "& .MuiFilledInput-input": {
                              height: 48,
                              boxSizing: "border-box",
                              paddingTop: 0,
                              paddingBottom: 0,
                            },
                            "& .MuiFormHelperText-root": { display: "none" },
                          }}
                        />
                      </Box>
                      <IconButton
                        edge={false}
                        tooltipText={BUTTON_ADD}
                        onClick={addVideo}
                        sx={{
                          ...gallerySecondaryCircleSurfaceSx(theme),
                          width: 48,
                          height: 48,
                          p: 0,
                          borderRadius: "50%",
                          flexShrink: 0,
                          "&:hover": {
                            backgroundColor: `${theme.palette.button.secondary.hover.main} !important`,
                          },
                        }}
                      >
                        <AddIcon
                          sx={{
                            fontSize: 24,
                            color: PRIMARY_COLOR_TEXT,
                            fill: PRIMARY_COLOR_TEXT,
                          }}
                        />
                      </IconButton>
                    </Box>
                    {formState.errors?.video?.message ? (
                      <Typography variant="caption" color="error">
                        {formState.errors.video.message}
                      </Typography>
                    ) : null}
                    {videos.length > 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          mt: 3,
                        }}
                      >
                        {videos.map((videoId) => (
                          <Box
                            key={videoId}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Link
                              href={youtubeVideoUrl(videoId)}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="body2"
                              sx={{ wordBreak: "break-all" }}
                            >
                              {youtubeVideoUrl(videoId)}
                            </Link>
                            <IconButton
                              edge={false}
                              tooltipText={BUTTON_DELETE}
                              onClick={() =>
                                setVideos((current) =>
                                  current.filter((item) => item !== videoId)
                                )
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    ) : null}
                  </Accordion>
                  <Accordion
                    title={t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_CONTENT_CATEGORIES)}
                    titleAlign="left"
                    initialValue={false}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "text.primary", fontSize: "14px" }}
                      >
                        {t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_SEARCH_CATEGORIES)}
                      </Typography>
                      <TextField
                        control={control}
                        name="contentLabelSearch"
                        rules={OPTIONAL_FIELD_RULES}
                        hiddenLabel
                        variant="filled"
                        fullWidth
                        sx={{
                          "& .MuiFilledInput-root": { height: 48 },
                          "& .MuiFilledInput-input": {
                            height: 48,
                            boxSizing: "border-box",
                            paddingTop: 0,
                            paddingBottom: 0,
                          },
                          "& .MuiFormHelperText-root": { display: "none" },
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        maxHeight: 280,
                        overflow: "auto",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {filteredContentLabels.map((label) => (
                        <Box
                          key={label.type}
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <Checkbox
                            checked={Boolean(label.excluded)}
                            onChange={(event) =>
                              setContentLabels((current) =>
                                current.map((item) =>
                                  item.type === label.type
                                    ? {
                                        ...item,
                                        excluded: event.target.checked,
                                      }
                                    : item
                                )
                              )
                            }
                          />
                          <Typography variant="body2">
                            {t(CONTENT_LABEL_KEYS[label.type], {
                              defaultValue: label.type,
                            })}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Accordion>
                </Box>
              ) : null}

              {section === "apps" ? (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <Accordion
                    title={t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_EXCLUDED_APPS)}
                    titleAlign="left"
                    initialValue={false}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                      }}
                    >
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          {t(APP_STORE_PLACEHOLDER)}
                        </FormLabel>
                        <RadioGroup
                          row
                          value={appStore || ""}
                          onChange={(event) => {
                            setValue("appStore", event.target.value);
                            setValue("appID", "");
                          }}
                        >
                          <FormControlLabel
                            value={GOOGLE_APP_STORE}
                            control={<Radio />}
                            label={t(APP_STORE_ANDROID)}
                          />
                          <FormControlLabel
                            value={APPLE_APP_STORE}
                            control={<Radio />}
                            label={t(APP_STORE_IOS)}
                          />
                        </RadioGroup>
                      </FormControl>
                      {appStore ? (
                        <SelectGoogleApp
                          control={control}
                          errors={formState.errors}
                          rules={OPTIONAL_FIELD_RULES}
                          name="appID"
                          label={t(APP_PLACEHOLDER)}
                          store={appStore}
                          fullWidth
                          variant="filled"
                          placeholder={t(APP_PLACEHOLDER)}
                          onChangeAction={onSelectExcludedApp}
                        />
                      ) : null}
                    </Box>
                    {apps.length > 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          mt: 3,
                        }}
                      >
                        {apps.map((app) => {
                          const storeLabelKey = mobileAppStoreLabelKey(
                            app.appId
                          );
                          return (
                            <Box
                              key={app.appId}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  minWidth: 0,
                                }}
                              >
                                {storeLabelKey ? (
                                  <ChipOptional
                                    size="small"
                                    label={t(storeLabelKey)}
                                    sx={{ flexShrink: 0 }}
                                  />
                                ) : null}
                                <Typography variant="body2">
                                  {app.name
                                    ? `${app.name} · ${app.appId}`
                                    : app.appId}
                                </Typography>
                              </Box>
                              <IconButton
                                edge={false}
                                tooltipText={BUTTON_DELETE}
                                onClick={() =>
                                  setApps((current) =>
                                    current.filter(
                                      (item) => item.appId !== app.appId
                                    )
                                  )
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : null}
                  </Accordion>
                  <Accordion
                    title={t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_APP_CATEGORIES)}
                    titleAlign="left"
                    initialValue={false}
                  >
                    <TextField
                      control={control}
                      name="categorySearch"
                      rules={OPTIONAL_FIELD_RULES}
                      label={t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_SEARCH_CATEGORIES)}
                      variant="filled"
                      fullWidth
                    />
                    <Box
                      sx={{
                        maxHeight: 280,
                        overflow: "auto",
                        display: "flex",
                        flexDirection: "column",
                        mt: 1,
                      }}
                    >
                      {filteredCategories.map((category) => (
                        <Box
                          key={category.resourceName}
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <Checkbox
                            checked={Boolean(category.excluded)}
                            onChange={(event) =>
                              setAppCategories((current) =>
                                current.map((item) =>
                                  item.resourceName === category.resourceName
                                    ? {
                                        ...item,
                                        excluded: event.target.checked,
                                      }
                                    : item
                                )
                              )
                            }
                          />
                          <Typography variant="body2">
                            {appCategoryLabel(category)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Accordion>
                </Box>
              ) : null}
            </Box>
          </>
        ) : null}
      </Box>
      <ContainerModalActions
        hasPadding={false}
        propsSecondaryButton={{
          showButton: true,
          text: BUTTON_CANCEL,
          onClick: onCloseModal,
        }}
        propsPrimaryButton={{
          showButton: !isLoading && !loadError,
          text: BUTTON_SAVE,
          onClick: () =>
            saveSection("all", {
              negativeKeywords: keywords,
              youtubeChannels: channels,
              youtubeVideos: videos,
              contentLabelTypes: contentLabels
                .filter((label) => label.excluded)
                .map((label) => label.type),
              mobileApps: apps,
              mobileAppCategoryConstants: appCategories
                .filter((category) => category.excluded)
                .map((category) => category.resourceName),
            }),
        }}
      />
    </ModalBasicLayout>
  );
};

export default GoogleAdsAccountExclusionsModal;
